<template>
  <div class="lz-form-card">
    <div class="lz-panel-title">
      <span class="lz-panel-icon-wrap"><b-icon icon="card-checklist"></b-icon></span>
      {{ inprocess ? 'Saving...' : 'Queued for Submission' }}
    </div>

    <div class="lz-empty-state" v-if="!holidayItems.length && !expenseItems.length">
      <b-icon icon="inbox" class="lz-empty-icon"></b-icon>
      <p>Nothing queued yet. use the form to add an entry.</p>
    </div>

    <div class="lz-queue-section" v-if="expenseItems.length">
      <h6 class="lz-queue-heading">Expenses</h6>
      <b-table
        responsive
        striped
        hover
        fixed
        small
        borderless
        table-class="lz-queue-table"
        class="mb-0"
        variant="info"
        :items="expenseItems"
        :fields="expenseFields">
        <template #cell(project)="data">
          {{
            data.item.expenseProjType === 'bd'
              ? data.item.project.split('_')[0]
              : data.item.project.split(':')[0]
          }}
        </template>
        <template #cell(date)="data">
          {{ formatDate(data.item.date) }}
        </template>
        <template #cell(amount)="data">
          {{ currencySymbol(data.item.currency)
          }}{{ parseFloat(data.item.amount).toFixed(2) }}
        </template>
        <template #cell(vat)="data"
          >{{ currencySymbol(data.item.currency)
          }}{{ parseFloat(data.item.vat).toFixed(2) }}</template
        >
        <template #cell(action)="data">
          <b-link
            v-if="data.value.status === 'queued' && showActions.expense.remove"
            class="lz-remove-link"
            @click="remove(data.value.id, 'expense')">
            <b-icon icon="x-circle-fill" title="Remove"></b-icon></b-link
          ><b-icon
            v-if="data.value.status === 'pending'"
            icon="hourglass-split"
            variant="info"
            title="Pending"></b-icon
          ><b-icon
            v-if="data.value.status === 'done'"
            icon="check-circle-fill"
            variant="success"
            title="Saved"></b-icon>
        </template>
      </b-table>
    </div>

    <div class="lz-queue-section" v-if="holidayItems.length">
      <h6 class="lz-queue-heading">Holidays</h6>
      <b-table
        responsive
        striped
        hover
        fixed
        small
        borderless
        table-class="lz-queue-table"
        class="mb-0"
        variant="secondary"
        :items="holidayItems"
        :fields="holidayFields">
        <template #cell(fromDate)="data">
          {{ formatDate(data.item.fromDate) }}
        </template>
        <template #cell(toDate)="data">
          {{ formatDate(data.item.toDate) }}
        </template>
        <template #cell(action)="data">
          <b-link
            v-if="data.value.status === 'queued' && showActions.holiday.remove"
            class="lz-remove-link"
            @click="remove(data.value.id, 'holiday')">
            <b-icon icon="x-circle-fill" title="Remove"></b-icon
          ></b-link>
          <b-icon
            v-if="data.value.status === 'pending'"
            icon="hourglass-split"
            variant="info"
            title="Pending"></b-icon>
          <b-icon
            v-if="data.value.status === 'done'"
            icon="check-circle-fill"
            variant="success"
            title="Saved"></b-icon>
        </template>
      </b-table>
    </div>

    <div class="lz-form-actions" v-if="holidayItems.length || expenseItems.length">
      <b-button
        size="sm"
        v-if="!inprocess"
        variant="info"
        class="lz-add-btn"
        @click="submitEntries">
        <b-icon icon="cloud-upload"></b-icon>
        <span
          >Submit
          {{ totalEntries }} {{ totalEntries === 1 ? 'entry' : 'entries' }}</span
        >
        <b-icon icon="cloud-upload" class="lz-add-btn-spacer" aria-hidden="true"></b-icon>
      </b-button>
      <b-button
        size="sm"
        disabled
        v-if="inprocess"
        variant="outline-secondary"
        class="lz-add-btn">
        <b-spinner small></b-spinner>
        <span>Please wait...</span>
        <b-spinner small class="lz-add-btn-spacer" aria-hidden="true"></b-spinner>
      </b-button>
    </div>
  </div>
</template>

<style scoped>
.lz-empty-state {
  text-align: center;
  padding: 44px 12px;
  color: #94a3b8;
}
.lz-empty-icon {
  font-size: 38px;
  margin-bottom: 14px;
  color: #cbd5e1;
}
.lz-empty-state p {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
}
.lz-queue-section {
  margin-bottom: 22px;
}
.lz-queue-section:last-of-type {
  margin-bottom: 0;
}
.lz-queue-heading {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 10px;
}
.lz-remove-link {
  color: #dc3545;
  margin: 0 3px;
}
.lz-remove-link:hover {
  color: #a71d2a;
}
</style>

<style>
.lz-queue-table {
  font-size: 14px !important;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.lz-queue-table thead tr {
  background-color: #0073be;
}
.lz-queue-table thead th {
  background: transparent !important;
  color: #fff !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: none !important;
  padding: 12px 12px !important;
  vertical-align: middle !important;
}
.lz-queue-table tbody td {
  padding: 12px !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
}
.lz-queue-table.b-table.b-table-fixed {
  table-layout: auto;
}
</style>

<script>
import ServicesOld from '../services/index.vue'
import { format as dateFormat } from 'date-fns'

export default {
  name: 'ActivityList',
  data() {
    return {
      inprocess: false,
      showList: false,
      showActions: {
        holiday: { remove: true },
        expense: { remove: true }
      },
      expenseItems: [],
      holidayItems: [],
      holidayFields: [
        {
          key: 'holidayLabel',
          label: 'Type'
        },
        {
          key: 'fromDate',
          label: 'From',
          sortable: true
        },
        {
          key: 'toDate',
          label: 'To'
        },
        {
          key: 'duration',
          label: 'Days'
        },
        {
          key: 'action',
          label: 'Actions',
          thClass: 'text-center',
          tdClass: 'text-center'
        }
      ],
      expenseFields: [
        {
          key: 'type',
          label: 'Type',
          sortable: true
        },
        {
          key: 'project',
          label: 'Project',
          sortable: true
        },
        {
          key: 'date',
          label: 'Date',
          sortable: true
        },
        {
          key: 'amount',
          label: 'Amount'
        },
        {
          key: 'vat',
          label: '+VAT'
        },
        {
          key: 'action',
          label: 'Actions',
          thClass: 'text-center',
          tdClass: 'text-center'
        }
      ]
    }
  },
  computed: {
    items() {
      return this.$store.getters.activityItems
    },
    totalEntries() {
      return this.holidayItems.length + this.expenseItems.length
    }
  },
  mounted() {
    this.listEntries()
  },
  methods: {
    listEntries() {
      if (this.inprocess) {
        return
      }
      let { expenseItems, holidayItems } = this.items
      this.expenseItems = expenseItems.map((itm, idx) => {
        itm.date = itm.dates[0]
        itm.action = {
          status: 'queued',
          id: idx
        }
        return itm
      })
      this.holidayItems = holidayItems.map((itm, idx) => {
        itm.fromDate = itm.dates[0]
        itm.toDate = itm.dates[1] || itm.dates[0]
        itm.haldDayMeridiem = itm.holidayHalfDayMeridiem
        itm.action = {
          status: 'queued',
          id: idx
        }
        return itm
      })
    },
    currencySymbol(currency) {
      let result = ''
      switch (currency) {
        case 'usd':
          result = '$'
          break
        case 'eur':
          result = '€'
          break
        default:
          result = '£'
      }
      return result
    },
    clearEntries() {
      this.$store.commit('clearActivityEnteries')
      this.holidayItems = []
      this.expenseItems = []
    },
    remove(id = 0, list = 'list') {
      if (!this.showActions[list].remove) {
        alert('Under Development!')
        return
      }
      list += 'Items'
      if (this[list].length === 1) {
        this[list] = []
      } else {
        this[list].splice(id, 1)
      }
      this.$store.commit('modifyActivityEnteries', {
        list: list,
        value: this[list]
      })
    },
    submitEntries() {
      try {
        const totalEntries = this.holidayItems.length + this.expenseItems.length
        this.inprocess = totalEntries.length
        if (!totalEntries) {
          this.inprocess = false
          return
        }
        let queue = new Array()
        queue = queue.concat(this.holidayItems)
        queue = queue.concat(this.expenseItems)
        queue = queue.filter((itm) => {
          return itm.action.status === 'queued'
        })
        queue.map((itm) => {
          itm.action.status = 'pending'
          return itm
        })
        setTimeout(() => {
          // console.log('saving-queue:', queue)
          this.sendEntry(queue)
        }, 500)
      } catch (err) {
        this.inprocess = false
        console.log(err)
      }
    },
    async sendEntry(queue, idx = 0) {
      let localVar = queue[idx]
      if (typeof localVar === 'undefined') {
        idx = idx < queue.length - 1 ? idx + 1 : 0
        this.inprocess = false
        return
      }
      try {
        let saved = null
        if (queue[idx].type === 'holiday') {
          saved = await ServicesOld.AddHoliday(queue[idx])
        } else if (queue[idx].type === 'expense') {
          saved = await ServicesOld.AddExpense(queue[idx])
        }
        localVar.action.status = 'queued'
        if (saved.status === 200) {
          localVar.action.status = 'done'
        }
        if (idx < queue.length - 1) {
          idx++
          return this.sendEntry(queue, idx)
        } else {
          this.inprocess = false
          this.resetList()
        }
      } catch (err) {
        const error =
          err?.response?.data?.error?.details?.validationErrors ?? []
        console.error(error)
        const defaultMsg =
          'Please review your entries and attempt the submission again.'
        this.$bvToast.toast(error.length ? error[0].message : defaultMsg, {
          title: String(
            'Error in saving ' + localVar?.type ?? 'Holiday'
          ).trim(),
          variant: 'danger',
          solid: true
        })
        if (idx < queue.length - 1) {
          idx++
          return this.sendEntry(queue, idx)
        } else {
          this.inprocess = false
        }
      }
    },
    resetList() {
      // this.$root.$emit('saved')
      this.expenseItems = []
      this.holidayItems = []
      this.clearEntries()
      this.$root.$emit('saved')
    },
    formatDate(date) {
      return dateFormat(new Date(date), 'dd/MM/yyyy')
    }
  },
  watch: {
    'items.expenseItems': {
      handler: 'listEntries',
      immediate: true
    },
    'items.holidayItems': {
      handler: 'listEntries',
      immediate: true
    }
  }
}
</script>
